import { Box, Paper, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Check } from 'lucide-react'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'

type CELExpressionActionItemProps = {
    expression: string
    explanation: string
    isValid: boolean
    onApply?: () => void
    hideExplanation?: boolean
}

/**
 * Renders a CEL expression result from the AI assistant.
 * Displays the expression code, explanation, and an "Apply" button.
 */
export const CELExpressionActionItem: FC<CELExpressionActionItemProps> = ({
    expression,
    explanation,
    isValid,
    onApply,
    hideExplanation,
}) => {
    if (isValid && !onApply) {
        // The expression and explanation are already in the message content
        // So without the onApply callback, we don't have to render anything
        return null
    }

    return <Paper
        variant="outlined"
        sx={{
            p: 2,
            bgcolor: 'common.white',
            borderRadius: C1BorderRadius.large,
            boxShadow: 'shadows.default',
        }}
    >
        <Stack spacing={2}>
            {/* CEL code block */}
            <Box sx={{
                bgcolor: 'grey.900',
                color: 'common.white',
                p: 1,
                borderRadius: C1BorderRadius.regular,
                overflow: 'auto',
            }}
            >
                <Typography variant="monospace">
                    {expression}
                </Typography>
            </Box>

            {/* Explanation */}
            {!hideExplanation && <Typography variant="caption">
                {explanation}
            </Typography>}

            {/* Validation status and Apply button */}
            <Stack direction="row" spacing={1} alignItems="center">
                {onApply && <C1Button
                    variant="outlined"
                    color="primary"
                    onClick={onApply}
                    startIcon={<C1Icon icon={Check} />}
                    disabled={!isValid}
                >
                    <FormattedMessage
                        id="copilot_cel_apply_to_editor"
                        defaultMessage="Apply to editor"
                    />
                </C1Button>}
                {!isValid && <Typography variant="caption" color="error.main">
                    <FormattedMessage
                        id="copilot_cel_validation_failed"
                        defaultMessage="Expression validation failed"
                    />
                </Typography>}
            </Stack>
        </Stack>
    </Paper>
}
